/**
 * API calls for the RLHF pairwise-comparison workbench.
 *
 * Auth is the app's cookie session, so every request carries `credentials: 'include'` — there is
 * no bearer token anywhere in this codebase.
 */

import { apiErrorMessage } from '../../utils/apiErrorMessage';

const API_URL = import.meta.env.VITE_API_URL || '';

/** Which report won. A forced choice — there is no tie, so every judged pair is directional. */
export type Verdict = 'a' | 'b';

export interface RatingPair {
  id: number;
  // Polymorphic item refs: a bare-integer simulation id ("42") or a chat-session ref ("chat_9").
  sim_a_id: string;
  sim_b_id: string;
  status: string;
  verdict: Verdict | null;
  remarks: string | null;
  assigned_at: string | null;
  submitted_at: string | null;
  updated_at: string | null;
}

export interface PairSummary {
  id: number;
  // Polymorphic item refs: bare-integer sim id or `chat_<id>`.
  sim_a_id: string;
  sim_b_id: string;
  sim_a_name: string | null;
  sim_b_name: string | null;
  status: string;
  verdict: Verdict | null;
  /** Carried so revisiting a judged pair restores the reasoning, not just the choice. */
  remarks: string | null;
  /** True once terminally handled — judged OR skipped-with-reason. */
  is_judged: boolean;
  /** Distinguishes a skip from a real verdict, so the queue can label it and show no winner. */
  is_skipped: boolean;
}

export interface PairProgress {
  judged: number;
  /** Terminal skips — counted apart from `judged` (a skip is not a verdict). */
  skipped: number;
  pending: number;
  available_pairs: number;
}

/** The pool has claimable pairs but every claim lost its race to a concurrent rater —
 *  a transient 503, distinct from a hard error, so the caller can retry instead of
 *  showing "something went wrong". */
export class PairsBusyError extends Error {
  constructor() {
    super('Finding your next comparison…');
    this.name = 'PairsBusyError';
  }
}

// /rlhf/pairs/next answers 503 under contention; retry a few times before surfacing it.
const NEXT_PAIR_BUSY_RETRIES = 3;
const NEXT_PAIR_BUSY_DELAY_MS = 1000;

/**
 * Turn a FastAPI error `detail` into something a rater can read.
 *
 * Our own routes raise HTTPException with a string, but a request that fails Pydantic validation
 * (e.g. a verdict the schema no longer accepts) comes back with `detail` as an ARRAY of error
 * objects. Interpolating that yields "[object Object]", which tells the rater nothing about work
 * that just failed to save.
 *
 * Now one line over the shared serialiser. This was the third of four hand-rolled versions of the
 * same function, and the payload that broke Review & Run (`detail` as an OBJECT carrying `error`)
 * was in none of their subsets — which is the argument for having exactly one. The `''` fallback is
 * this module's own: the caller appends the status code when there is no sentence.
 */
const describeDetail = (detail: unknown): string => apiErrorMessage(detail, '');

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (response.status === 403) {
    throw new Error('You do not have rater access. Ask an admin to enable it for your account.');
  }
  // 503 = the pool is non-empty but we lost every claim race — transient, retryable.
  if (response.status === 503) {
    throw new PairsBusyError();
  }
  if (!response.ok) {
    // Surface the backend's own message where it has one — the verdict endpoint explains
    // exactly why a submission was rejected (no choice made, reasoning too short).
    let detail = '';
    try {
      detail = describeDetail((await response.json())?.detail);
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `Request failed (${response.status})`);
  }
  // 204 = no further pair can be formed.
  if (response.status === 204) return null as T;
  return response.json();
}

/** Advance to the next comparison needing judgment. Null when no pair can be formed.
 *  Retries the transient 503 (pool busy, all claims lost their race) a few times before
 *  giving up, so a rater under contention isn't shown a spurious error.
 *  `excludeSimulationIds` is passed by Skip (the two sims of the pair just released) so the
 *  rater is not immediately re-paired on the same reports. */
export const fetchNextPair = async (
  excludeSimulationIds?: string[],
): Promise<RatingPair | null> => {
  const qs = excludeSimulationIds?.length
    ? '?' + excludeSimulationIds
        .map((id) => `exclude_simulation_id=${encodeURIComponent(id)}`).join('&')
    : '';
  for (let attempt = 0; ; attempt++) {
    try {
      return await request<RatingPair | null>(`/rlhf/pairs/next${qs}`, { method: 'POST' });
    } catch (e) {
      if (e instanceof PairsBusyError && attempt < NEXT_PAIR_BUSY_RETRIES) {
        await new Promise((r) => setTimeout(r, NEXT_PAIR_BUSY_DELAY_MS));
        continue;
      }
      throw e;
    }
  }
};

/** The rater's whole queue, oldest first — drives Prev/Next and revisiting. */
export const fetchPairs = () =>
  request<{ pairs: PairSummary[] }>('/rlhf/pairs').then((r) => r.pairs);

export const fetchPairProgress = () => request<PairProgress>('/rlhf/pairs/progress');

/**
 * Save or revise a verdict. Idempotent — this is what makes both autosave and after-the-fact
 * editing work. `draft` persists partial work without marking the pair judged.
 */
export const saveVerdict = (
  pairId: number,
  body: { verdict: Verdict | null; remarks: string; draft: boolean },
) =>
  request<RatingPair>(`/rlhf/pairs/${pairId}/verdict`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const releasePair = (pairId: number) =>
  request<{ released: boolean }>(`/rlhf/pairs/${pairId}/release`, { method: 'POST' });

/**
 * Terminally skip a pair with a required reason. Unlike release, the pair is closed out and its
 * simulations are never re-paired — how a rater declines a comparison without it looping back.
 */
export const skipPair = (pairId: number, reason: string) =>
  request<RatingPair>(`/rlhf/pairs/${pairId}/skip`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
